import { ofType, combineEpics } from "redux-observable";
import { of, concat, empty } from "rxjs";
import { catchError, map, mergeMap, tap } from "rxjs/operators";
import { default as ajax } from "../../common/services/utils";
import { push } from "connected-react-router";
import { apiUrl } from "../../common/services/utils";
import actionCreators from "./actionCreators";
import registerActionCreators from "./Register/actionCreators";
import errorHandler from "../../common/services/ajaxErrorHandler";
import { switchCase } from "../../common/services/utils";
import { default as UIActionCreators } from "../../store/MainView/actionCreators";
import { default as eventActionCreators } from "../../store/Calendar/Event/actionCreators";
import { default as notificationsActionCreators } from "../../store/Notifications/actionCreators";
import { default as myProgrammeActionCreators } from "../../store/MyProgramme/actionCreators";
import { default as calendarActionCreators } from "../../store/Calendar/FullList/actionCreators";
import GTM from "../../common/services/GTM";

const authStartLoadingEpic = action$ =>
    action$.pipe(
        ofType(
            actionCreators.checkStorage.type,
            actionCreators.logout.type,
            actionCreators.interactiveLogin.type
        ),
        mergeMap(() => of(UIActionCreators.setLoading.create()))
    );

const authClearLoadingEpic = action$ =>
    action$.pipe(
        ofType(
            actionCreators.setAuthAndRegistered.type,
            actionCreators.setAuthNotRegistered.type,
            actionCreators.redirectToHome.type,
            actionCreators.errorResponse.type
        ),
        mergeMap(() => of(UIActionCreators.clearLoading.create()))
    );

const samlValidateEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.samlValidate.type),
        mergeMap(action =>
            ajax
                .post(apiUrl("auth/v2/frontend/exchange-code"), action.payload, {
                    "Content-Type": "application/json",
                })
                .pipe(
                    map(res => ({ data: res.response, status: res.status })),
                    mergeMap(res => {
                        if (res.status === 200 || res.status === 201) {
                            localStorage.removeItem("lshf_auth_state");
                            localStorage.removeItem("lshf_auth_code_verifier");

                            localStorage.setItem("token", res.data.id);
                        }
                        if (res.status === 200) {
                            localStorage.setItem("user", JSON.stringify(res.data.user));
                        }
                        const uid = localStorage.getItem("uid");
                        const isActionPost = localStorage.getItem("redirectAction") === "post";
                        const redirectUrl = localStorage.getItem("redirect")
                            ? localStorage.getItem("redirect")
                            : "/feed";

                        GTM.setUserId(res.data.user.id);

                        return switchCase({
                            200: concat(
                                of(actionCreators.clearRedirects.create()),
                                of(actionCreators.setAuthAndRegistered.create()),
                                of(actionCreators.setUser.create(res.data.user)),
                                of(
                                    notificationsActionCreators.getUnseenNotificationsCount.create()
                                ),
                                of(myProgrammeActionCreators.getStudentsList.create()),
                                isActionPost
                                    ? of(eventActionCreators.confirmEvent.create(uid))
                                    : empty(),
                                of(push(redirectUrl))
                            ),
                            201: concat(
                                of(actionCreators.clearRedirects.create()),
                                of(actionCreators.setAuthNotRegistered.create()),
                                of(registerActionCreators.nextStep.create()),
                                of(actionCreators.setUser.create(res.data.user)),
                                of(actionCreators.requireRegistrationsTerms.create()),
                                of(push("/welcome"))
                            ),
                            204: concat(
                                of(
                                    actionCreators.errorResponse.create(
                                        "Something seems to have gone wrong - we're experiencing some technical difficulties. Please try again later!"
                                    )
                                ),
                                of(push("/welcome"))
                            ),
                        })()(res.status);
                    }),
                    catchError(res => {
                        // handle validation errors ...
                        if (res.status === 403) {
                            return concat(
                                of(actionCreators.errorResponse.create(res.response.error)),
                                of(push("/welcome"))
                            );
                        } else {
                            return concat(
                                of(
                                    actionCreators.errorResponse.create(
                                        "Authentication failed - an unexpected error occurred"
                                    )
                                ),
                                of(push("/welcome"))
                            );
                        }
                    })
                )
        )
    );
const clearRedirectsEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.clearRedirects.type),
        tap(() => {
            localStorage.removeItem("redirect");
            localStorage.removeItem("uid");
            localStorage.removeItem("redirectAction");
        }),
        mergeMap(() => {
            return empty();
        })
    );

const checkStorageEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.checkStorage.type),
        mergeMap(() => {
            const token = localStorage.getItem("token");
            const user = JSON.parse(localStorage.getItem("user"));
            const isMasqueraded = localStorage.getItem("isMasqueraded");
            const calendarLast = localStorage.getItem("calendarLast");

            if (token && user && !!user.email) {
                return concat(
                    of(actionCreators.setAuthAndRegistered.create()),
                    of(myProgrammeActionCreators.getStudentsList.create()),
                    of(actionCreators.setUser.create(user)),
                    of(notificationsActionCreators.getUnseenNotificationsCount.create()),
                    calendarLast
                        ? of(
                              calendarActionCreators.setDefaultStatus.create(
                                  calendarLast === "list"
                              )
                          )
                        : empty(),
                    of(actionCreators.setMasqueraded.create(!!isMasqueraded))
                );
            } else if (token && user && !user.email) {
                return concat(
                    of(actionCreators.setAuthNotRegistered.create()),
                    of(actionCreators.setUser.create(user))
                );
            } else {
                return of(actionCreators.logout.create());
            }
        })
    );

const logoutEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.logout.type),
        tap(() => {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("isMasqueraded");
        }),
        mergeMap(() => {
            return concat(
                of(actionCreators.reset.create()),
                of(registerActionCreators.reset.create()),
                of(actionCreators.redirectToHome.create())
            );
        })
    );

const redirectToHomeEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.redirectToHome.type),
        mergeMap(() => concat(of(push("/welcome"))))
    );

const interactiveLoginEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.interactiveLogin.type),
        mergeMap(action =>
            ajax
                .post(apiUrl("auth/interactive-login"), action.payload, {
                    "Content-Type": "application/json",
                })
                .pipe(
                    map(res => ({ data: res.response, status: res.status })),
                    mergeMap(res => {
                        if (res.status === 200) {
                            localStorage.setItem("token", res.data.id);
                            localStorage.setItem("user", JSON.stringify(res.data.user));
                            localStorage.setItem("isMasqueraded", 1);
                        }
                        GTM.setUserId(res.data.user.id);

                        return switchCase({
                            200: concat(
                                of(actionCreators.setAuthAndRegistered.create()),
                                of(actionCreators.setUser.create(res.data.user)),
                                of(
                                    notificationsActionCreators.getUnseenNotificationsCount.create()
                                ),
                                of(myProgrammeActionCreators.getStudentsList.create()),
                                of(actionCreators.setMasqueraded.create(true)),
                                of(push("/"))
                            ),
                            204: of(
                                actionCreators.errorResponse.create({
                                    errorRes:
                                        "Something seems to have gone wrong - we're experiencing some technical difficulties. Please try again later!",
                                })
                            ),
                        })()(res.status);
                    }),
                    catchError(errorHandler(actionCreators.errorResponse.create))
                )
        )
    );

export const epics = combineEpics(
    authStartLoadingEpic,
    authClearLoadingEpic,
    samlValidateEpic,
    checkStorageEpic,
    logoutEpic,
    redirectToHomeEpic,
    interactiveLoginEpic,
    clearRedirectsEpic
);
